import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Link2, Download, Rocket, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Tag } from "@/components/Shell";

const MODES = [
  ["full_xero", "Full Xero", "Xero invoices commit stock and POs push bills to Xero."],
  ["xero_purchases", "Xero purchases only", "POs and bills sync to Xero; sales and stock-out live in Stock360."],
  ["spreadsheet", "Spreadsheet only", "No Xero — products, stock, purchases and sales all in Stock360 and Excel."],
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [csv, setCsv] = useState("");
  const [productCsv, setProductCsv] = useState("");
  const [locName, setLocName] = useState("Main Warehouse");
  const [locCode, setLocCode] = useState("MAIN");
  const [rowErrors, setRowErrors] = useState([]);

  const load = async () => {
    const { data } = await api.get("/onboarding/status");
    setStatus(data);
    return data;
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const run = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      await load();
      await refresh();
      if (successMsg) toast.success(successMsg);
      return res;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const loadTemplate = async () => {
    const { data } = await api.get("/onboarding/opening-stock-template");
    setCsv(data.csv);
    toast.success("Template loaded — edit quantities then upload");
  };

  const uploadProducts = async () => {
    setBusy("products");
    try {
      const { data } = await api.post("/uploads/preview", { kind: "products", csv_text: productCsv });
      const valid = data.rows.filter((r) => r.valid);
      const bad = data.rows.filter((r) => !r.valid);
      if (!valid.length) {
        setRowErrors(bad.map((r) => ({ row: r.row, error: r.errors.join("; ") })));
        toast.error("No valid rows — check the errors below");
        return;
      }
      await api.post("/uploads/commit", { kind: "products", filename: "onboarding products", rows: valid.map((r) => r.values) });
      setRowErrors(bad.map((r) => ({ row: r.row, error: r.errors.join("; ") })));
      toast.success(`${valid.length} product(s) added`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const mode = status?.operating_mode || "full_xero";
  const active = status?.active_step;
  const steps = status?.steps || [];
  const allDone = steps.length > 0 && steps.every((s) => s.complete);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="onboarding-page">
      <header className="border-b border-slate-200 bg-white px-5 py-4">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center bg-slate-900 text-[11px] font-bold text-white">S3</span>
            <span className="font-display font-semibold">Stock360 setup</span>
          </div>
          {mode !== "spreadsheet" && (
            <Btn
              variant="outline"
              data-testid="load-demo-data-button"
              disabled={busy === "demo"}
              onClick={() => run("demo", () => api.post("/demo/seed"), "Demo org loaded").then(() => navigate("/app"))}
            >
              <Rocket className="h-4 w-4" /> {busy === "demo" ? "Loading demo…" : "Load demo data"}
            </Btn>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-5 py-10">
        <h1 className="font-display text-3xl font-semibold">Get live in under ten minutes</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          First, how much should Stock360 share with Xero? You can change this later in Settings.
        </p>

        <div className="mt-6 grid gap-px bg-slate-200 md:grid-cols-3" data-testid="onboarding-mode-picker">
          {MODES.map(([value, label, desc]) => {
            const isActive = mode === value;
            return (
              <button
                key={value}
                data-testid={`onboarding-mode-${value}`}
                disabled={busy === "mode"}
                onClick={() => run("mode", () => api.put("/onboarding/mode", { operating_mode: value }), `Mode set to ${label}`)}
                className={`p-4 text-left transition-colors ${isActive ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
              >
                <p className="font-display text-sm font-semibold">{label}</p>
                <p className={`mt-1 text-xs ${isActive ? "text-slate-300" : "text-slate-500"}`}>{desc}</p>
                {isActive && <span className="mt-2 inline-block bg-white px-2 py-0.5 text-[10px] uppercase tracking-widest text-slate-900">Selected</span>}
              </button>
            );
          })}
        </div>

        {mode !== "full_xero" && (
          <p className="mt-3 text-xs text-slate-500" data-testid="onboarding-mode-note">
            {mode === "xero_purchases"
              ? "Sales will be recorded in Stock360 — you'll commit stock to a sale reference and issue against it."
              : "No Xero connection needed. Add products and stock from Excel, then record sales in Stock360."}
          </p>
        )}

        <ol className="mt-8 space-y-px border border-slate-200 bg-slate-200" data-testid="onboarding-steps">
          {steps.map((step, i) => (
            <li key={step.id} className="bg-white px-5 py-4" data-testid={`onboarding-step-${step.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className={`num grid h-6 w-6 place-items-center text-xs ${
                    step.complete ? "bg-emerald-600 text-white" : active === step.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    {step.complete ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className={`text-sm ${active === step.id ? "font-medium text-slate-900" : "text-slate-600"}`}>{step.label}</span>
                </div>
                {step.complete && <Tag tone="green">Done</Tag>}
              </div>

              {active === step.id && (
                <div className="mt-4 pl-9">
                  {step.id === "connect" && (
                    <div>
                      <p className="text-sm text-slate-600">
                        Connects with OAuth 2.0. This build uses a mocked Xero tenant so you can see
                        the whole flow — live keys drop in via env vars.
                      </p>
                      <Btn className="mt-3" data-testid="connect-xero-button" disabled={busy === "connect"}
                        onClick={() => run("connect", () => api.post("/xero/connect"), "Connected to Xero")}>
                        <Link2 className="h-4 w-4" /> {busy === "connect" ? "Connecting…" : "Connect to Xero"}
                      </Btn>
                    </div>
                  )}
                  {step.id === "import" && (
                    <div>
                      <p className="text-sm text-slate-600">
                        Pulls items, contacts and tax rates. Xero tax rates take precedence over the
                        seeded UK VAT rates.
                      </p>
                      <Btn className="mt-3" data-testid="import-xero-button" disabled={busy === "import"}
                        onClick={() => run("import", () => api.post("/xero/import"), "Import complete")}>
                        {busy === "import" ? "Importing…" : "Import from Xero"}
                      </Btn>
                    </div>
                  )}
                  {step.id === "accounts" && (
                    <div>
                      <p className="text-sm text-slate-600">Adjustment and COGS journals post to these accounts.</p>
                      <Btn className="mt-3" data-testid="map-accounts-button" disabled={busy === "accounts"}
                        onClick={() => run("accounts", () => api.post("/onboarding/accounts", {
                          inventory_account: "630", cogs_account: "310",
                          adjustment_account: "500", revenue_account: "200",
                        }), "Accounts mapped")}>
                        Use recommended mapping (630 / 310 / 500 / 200)
                      </Btn>
                    </div>
                  )}
                  {step.id === "location" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <label className="text-sm">
                        Location name
                        <input data-testid="location-name-input" className="mt-1 block border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                          value={locName} onChange={(e) => setLocName(e.target.value)} />
                      </label>
                      <label className="text-sm">
                        Code
                        <input data-testid="location-code-input" className="mono mt-1 block w-28 border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                          value={locCode} onChange={(e) => setLocCode(e.target.value.toUpperCase())} />
                      </label>
                      <Btn data-testid="create-location-button" disabled={busy === "loc"}
                        onClick={() => run("loc", () => api.post("/locations", { name: locName, code: locCode, is_default: true }), "Location created")}>
                        Create location
                      </Btn>
                    </div>
                  )}
                  {step.id === "products" && (
                    <div>
                      <p className="text-sm text-slate-600">
                        Paste your product list — columns sku, name, unit_cost_pence,
                        sale_price_pence, reorder_point. Headers are matched loosely.
                      </p>
                      <textarea
                        data-testid="onboarding-products-textarea"
                        value={productCsv}
                        onChange={(e) => setProductCsv(e.target.value)}
                        rows={6}
                        placeholder={"sku,name,unit_cost_pence,sale_price_pence,reorder_point\nGRD-900-BLK,900W Coffee Grinder,2100,4499,25"}
                        className="mono mt-3 w-full border border-slate-300 p-3 text-xs focus:border-slate-900 focus:outline-none"
                      />
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Btn data-testid="onboarding-products-upload" disabled={busy === "products" || !productCsv.trim()} onClick={uploadProducts}>
                          <FileSpreadsheet className="h-4 w-4" /> {busy === "products" ? "Adding…" : "Validate & add products"}
                        </Btn>
                        <Btn variant="outline" data-testid="onboarding-products-sample"
                          onClick={() => setProductCsv("sku,name,unit_cost_pence,sale_price_pence,reorder_point\nGRD-900-BLK,900W Coffee Grinder,2100,4499,25\nJUG-18-GLS,1.8L Glass Water Jug,640,1499,40")}>
                          Load sample rows
                        </Btn>
                      </div>
                    </div>
                  )}
                  {step.id === "opening_stock" && (
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        {mode !== "spreadsheet" && (
                          <Btn variant="outline" data-testid="load-template-button" onClick={loadTemplate}>
                            <Download className="h-4 w-4" /> Load CSV template
                          </Btn>
                        )}
                        <span className="text-xs text-slate-500">Columns: sku, quantity, unit_cost_pence</span>
                      </div>
                      <textarea
                        data-testid="opening-stock-textarea"
                        value={csv}
                        onChange={(e) => setCsv(e.target.value)}
                        rows={8}
                        placeholder="sku,quantity,unit_cost_pence"
                        className="mono mt-3 w-full border border-slate-300 p-3 text-xs focus:border-slate-900 focus:outline-none"
                      />
                      <Btn className="mt-3" data-testid="upload-opening-stock-button" disabled={busy === "stock" || !csv.trim()}
                        onClick={async () => {
                          const res = await run("stock", () => api.post("/onboarding/opening-stock", { csv_text: csv }));
                          if (res) {
                            setRowErrors(res.data.errors || []);
                            toast.success(`${res.data.applied.length} rows applied`);
                          }
                        }}>
                        {busy === "stock" ? "Uploading…" : "Validate & upload"}
                      </Btn>
                    </div>
                  )}
                  {rowErrors.length > 0 && (
                    <div className="mt-3 border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="opening-stock-errors">
                      {rowErrors.map((r) => (<p key={r.row}>Row {r.row}: {r.error}</p>))}
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>

        {status?.tracked_items_detected > 0 && (
          <div className="mt-6 flex items-start gap-3 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="tracked-items-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">{status.tracked_items_detected} Xero items are tracked as inventory</p>
              <p className="mt-1">
                Convert these to untracked in Xero so quantities aren't counted twice. Stock360 will
                keep posting the adjustment and COGS journals for you.
              </p>
            </div>
          </div>
        )}

        {allDone && (
          <div className="mt-6 flex flex-wrap items-center gap-3 border border-slate-200 bg-white p-5">
            <p className="flex-1 text-sm text-slate-600">
              Setup complete — {status?.counts?.products ?? 0} products, {status?.counts?.locations ?? 0} location(s).
            </p>
            <Btn data-testid="finish-onboarding-button"
              onClick={() => run("done", () => api.post("/onboarding/complete")).then(() => navigate("/app"))}>
              Go to dashboard
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}
